<template>
  <div
    class="tiptap-wrap"
    :class="{
      'read-only': isReadOnly,
      'has-content': !isEmpty,
    }"
    @copy="onCopy"
    @paste="onPaste"
  >
    <HighlightTooltip
      :target="activeHighlightTooltipTarget || activeNudgeTooltipTarget"
      :tooltip-props="{ tooltips: activeTooltips }"
      :close-on-content-click="true"
    >
      <template #default="{ tooltipProps }">
        <div :style="{ 'max-width': tooltipProps.tooltips[0]?.isNudge ? '360px' : '240px' }">
          <HighlightTooltipContent
            :tooltips="tooltipProps.tooltips"
            @delete-click="onTooltipButtonClick({ id: $event, type: 'delete' })"
            @learn-more-click="onTooltipButtonClick({ id: $event, type: 'readMore' })"
            @suggestion-click="onTooltipButtonClick({ ...$event, type: 'suggestion' })"
            @ai-suggest-click="onAiSuggestClick({ id: $event })"
            @apply-template-click="onTooltipButtonClick({ id: $event, type: 'applyTemplate' })"
          />
        </div>
      </template>
    </HighlightTooltip>

    <div class="d-flex">
      <span
        v-if="emptyHighlight && tiptap && !tiptap.getText()"
        :class="[`hl-${emptyHighlight.className}`, emptyHighlight.action]"
        :data-highlight-id="emptyHighlight.id"
        @mouseover="onHighlightMouseOver({ id: emptyHighlight.id, event: $event })"
        @mouseout="onHighlightMouseOut"
      />

      <div class="flex-grow-1">
        <EditorContent :editor="tiptap" />
      </div>
    </div>
  </div>
</template>

<script>
import { decode } from 'he';
import { v4 as uuidv4 } from 'uuid';
import { debounce } from 'lodash-es';
import { Editor, EditorContent } from '@tiptap/vue-3';
import { generateHTML } from '@tiptap/html';
import HighlightTooltip from '@ui/components/editor/HighlightTooltip/HighlightTooltip.vue';
import HighlightTooltipContent from '@ui/components/editor/HighlightTooltip/HighlightTooltipContent.vue';
import { extensions } from './extensions';
import { tiptapWrapProps } from './tiptapWrapProps';

export default {
  name: 'TiptapWrap',
  components: {
    EditorContent, HighlightTooltip, HighlightTooltipContent,
  },
  props: tiptapWrapProps,
  emits: [
    'beforeCreate',
    'create',
    'selectionUpdate',
    'transaction',
    'focus',
    'blur',
    'destroy',
    'update:modelValue',
    'trackApplyHighlight',
    'trackApplyNudge',
    'trackViewHighlight',
    'trackViewNudge',
    'copyContent',
    'pasteContent',
    'linkEdit',
    'showApplyTemplate',
    'aiSuggestClick',
    'focusEditor',
    'blurEditor',
    'removeEditor',
    'updateSelectedJson',
    'addEditor',
  ],

  data() {
    return {
      tiptap: null,
      activeHighlightId: '',
      mouseX: 0,
      mouseY: 0,
      isEmpty: true,
      activeNudgeId: '',
    };
  },
  computed: {
    // title, company, location values are plain strings. they must be wrapped into <p>
    wrappedValue() {
      if (!this.value || this.value[0] !== '<') {
        return `<p>${this.value}</p>`;
      }
      return this.value;
    },

    filteredExtensions() {
      return extensions({ getTiptapWrap: () => this, disabledNodes: this.disabledNodes });
    },

    activeHighlight() {
      if (this.activeHighlightId) {
        const activeHighlight = this.highlights
          .find((highlight) => highlight.id === this.activeHighlightId) || {};
        return { ...activeHighlight, isEditable: !this.isReadOnly };
      }
      return {};
    },

    emptyHighlight() {
      return this.highlights.find((hl) => !hl.highlightedText);
    },

    activeNudges() {
      if (!this.activeNudgeId) {
        return [];
      }
      const { messages } = this.nudges.find(({ block }) => block.id === this.activeNudgeId) || {};
      return messages || [];
    },

    activeHighlightTooltipTarget() {
      return document.querySelector(`[data-highlight-id="${this.activeHighlightId}"]`);
    },

    activeNudgeTooltipTarget() {
      return document.querySelector(`[data-nudge-id="${this.activeNudgeId}"] .nudge-icon`);
    },

    activeTooltips() {
      return ((this.activeHighlightId ? [this.activeHighlight] : this.activeNudges) || []).map((item) => ({
        id: item.id,
        isNudge: item.is_nudge,
        aiSuggest: item.action === 'ai_replace',
        category: item.className,
        description: item.tooltip_long,
        title: item.tooltip_short,
        suggestions: item.suggestions?.map((suggestion) => ({ text: suggestion })),
        severity: item.className,
        deleteButton: item.action === 'delete' || item.action === 'replace_or_delete',
        showLearnMore: Boolean(item.readMoreUrl),
        readOnly: this.isReadOnly,
        isLoading: this.aiSuggestionsLoading,
        aiSuggestions: this.aiSuggestions,

        // 102 - Incorrect Template Content
        // 103 - No Template Content
        showApplyTemplate: [102, 103].includes(item.childCategoryId),
      }));
    },
  },

  watch: {
    value() {
      const { tiptap, wrappedValue } = this;

      if (!tiptap) {
        return;
      }

      this.isEmpty = this.tiptap.isEmpty;

      // wrappedValue is what has been passed through props
      // html is what tiptap has at the moment
      const { html } = this.getCleanJsonAndHtml();
      if (decode(html) !== decode(wrappedValue)) {
        const { anchor, head } = tiptap.view.state.selection;
        this.tiptap.chain()
          .setContent(wrappedValue, {
            emitUpdate: false,
            parseOptions: { preserveWhitespace: true },
          })
          .setTextSelection({ from: anchor, to: head })
          .run();
      }
    },

    isReadOnly(readOnly) {
      this.tiptap.setEditable(!readOnly);
    },

    highlights: {
      immediate: true,
      deep: true,
      handler() {
        if (!this.tiptap) {
          return;
        }
        this.updateHighlights();
      },
    },

    nudges: {
      immediate: true,
      deep: true,
      handler() {
        if (!this.tiptap) {
          return;
        }
        this.updateNudges();
      },
    },
  },

  mounted() {
    this.initTiptap();
  },

  beforeUnmount() {
    this.destroyTiptap();
  },

  methods: {
    initTiptap() {
      this.tiptap = new Editor({
        content: this.wrappedValue,
        extensions: this.filteredExtensions,
        onUpdate: this.onTiptapUpdate,
        onSelectionUpdate: this.onSelectionUpdate,
        editorProps: {
          handleClickOn: (view, pos, node, nodePos, event) => {
            /* Recursively checks the dom to see if the clicked on
            element is in a link within the prosemirror element */
            const checkIfIsPartOfLink = (element) => {
              if (!element || element.classList.contains('ProseMirror')) return false;

              return element.tagName === 'A' || checkIfIsPartOfLink(element.parentNode);
            };

            const isPartOfLink = checkIfIsPartOfLink(event.target);

            if (this.tiptap.isActive('link') && isPartOfLink && this.tiptap.isEditable) {
              this.eventPassThroughHandler('linkEdit', event);
            }
          },
        },

        ...this.tiptapConfigOverride,
      });

      [
        'beforeCreate', 'create', 'selectionUpdate', 'transaction', 'focus', 'blur', 'destroy',
      ].forEach((eventName) => this.tiptap.on(
        eventName,
        (event) => this.eventPassThroughHandler(eventName, event),
      ));

      this.tiptap.setEditable(!this.isReadOnly);
      this.updateHighlights();
      this.updateNudges();

      const { editorId, tiptap } = this;
      this.$emit('addEditor', { editorId, tiptap });
      tiptap.on('focus', () => this.$emit('focusEditor', { editorId }));
      tiptap.on('linkEdit', (event) => this.eventPassThroughHandler('linkEdit', event));

      // show highlights within selection range when clicked outside the editor
      tiptap.on('blur', () => {
        this.updateHighlights({ blur: true });
        this.$emit('blurEditor', { blur: true, extensions: this.filteredExtensions });
      });
    },

    destroyTiptap() {
      this.$emit('removeEditor', { editorId: this.editorId });
      if (this.tiptap) {
        this.tiptap.destroy();
      }
    },

    updateHighlights({ blur } = {}) {
      this.tiptap.commands.setHighlights(this.highlights, {
        blur,
        focus: this.editorId === this.focusedEditorId,
      });
    },

    updateNudges() {
      this.tiptap.commands.setNudges(this.nudges);
    },

    getHighlightData(message) {
      if (message.fieldId === 'title') {
        // title may have multiple "insert_after_hl" or "insert_before_hl" highlights.
        // we can't render multiple plus signs for them, so we join same offset+action highlights
        // to be displayed as one multi-page tooltip
        return this.highlights
          .filter((hl) => hl.offset === message.offset
            && hl.action === message.action
            && hl.fieldId === 'title')
          .map((hl) => ({ ...hl, isEditable: !this.isReadOnly }));
      }
      return [{ ...message, isEditable: !this.isReadOnly }];
    },

    onTiptapUpdate(event) {
      // highlight update should not trigger 'input' event as no actual content change happened
      if (event.transaction?.getMeta('contentNotUpdated')) {
        return;
      }

      // Close highlight tooltip suggestions when typing new input
      this.onHighlightMouseOut();

      const { json, html } = this.getCleanJsonAndHtml();
      this.eventPassThroughHandler('update:modelValue', {
        ...event,
        html,
        json,
        blocks: this.getBlocksForRescore(json),
      });
    },

    onSelectionUpdate: debounce(function onSelectionUpdateDebounced() {
      this.$emit('updateSelectedJson', { editorId: this.editorId, extensions: this.filteredExtensions });
      this.updateHighlights();
    }, 200),

    eventPassThroughHandler(eventName, event) {
      this.$emit(eventName, { editorId: this.editorId, ...event });
    },

    onAiSuggestClick({ id }) {
      const activeHighlight = this.highlights.find((highlight) => highlight.id === id) || {};
      this.$emit('aiSuggestClick', {
        id,
        ruleId: activeHighlight.ruleId,
        text: activeHighlight.highlightedText,
        action: activeHighlight.action,
        className: activeHighlight.className,
      });
    },

    onTooltipButtonClick({ id, type, suggestion = '' }) {
      const activeNudge = this.nudges.find((nudge) => nudge.block?.id === id);
      if (activeNudge) {
        this.onNudgeTooltipButtonClick({ id, suggestion });
        return;
      }

      const activeHighlight = this.highlights.find((highlight) => highlight.id === id) || {};
      if (type === 'readMore') {
        this.openUrl(activeHighlight.readMoreUrl);
      } else if (type === 'applyTemplate') {
        this.eventPassThroughHandler('showApplyTemplate', { id });
      } else {
        this.tiptap.commands.replaceHighlight({ id, suggestion });
      }

      this.eventPassThroughHandler('trackApplyHighlight', {
        suggestion,
        action: activeHighlight.action,
        highlightedText: activeHighlight.highlightedText,
        readMoreUrl: activeHighlight.readMoreUrl,
        type,
        className: activeHighlight.className,
        suggestions: activeHighlight.suggestions,
      });
    },

    openUrl(url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    },

    onNudgeTooltipButtonClick(event) {
      const { id, suggestion } = event;
      this.tiptap.commands.insertNudgeSuggestion({ id, suggestion });
      this.updateNudges();
      this.eventPassThroughHandler('trackApplyNudge', event);
    },

    onHighlightMouseOver({ id, event }) {
      this.mouseX = event.clientX;
      this.mouseY = event.clientY;
      this.activeHighlightId = id;

      this.eventPassThroughHandler('trackViewHighlight', { ...this.activeHighlight });
    },

    onHighlightMouseOut() {
      this.activeHighlightId = '';
    },

    onNudgeMouseOver({ id }) {
      this.activeNudgeId = id;
      this.eventPassThroughHandler('trackViewNudge', { ...this.activeNudges[0] });
    },

    onNudgeMouseOut() {
      this.activeNudgeId = '';
    },

    removeHighlightsAndLinkAttributesFromJson(json) {
      /* eslint-disable no-param-reassign */
      const goDeeper = (node, topNode = {}, depth = 0) => {
        if (depth > 1000) {
          return;
        }
        if (node.marks) {
          if (topNode?.type === 'heading') {
            node.marks = node.marks
              .filter((mark) => !['italic', 'bold', 'link'].includes(mark.type));
          }

          node.marks = node.marks.filter((mark) => mark.type !== 'highlightSuggestion');

          // for each link remove all attributes except href, target and rel
          node.marks = node.marks.map((mark) => {
            if (mark.type === 'link') {
              mark.attrs = {
                href: mark.attrs.href,
                target: mark.attrs.target,
                rel: mark.attrs.rel,
              };
            }
            return mark;
          });
        }

        if (node.attrs) {
          delete node.attrs.nudgeId;
          delete node.attrs.severity;
          delete node.attrs.count;
        }

        (node.content || []).forEach((child) => {
          goDeeper(child, depth === 1 ? node : topNode, depth + 1);
        });
        /* eslint-enable no-param-reassign */
      };

      goDeeper(json);
      return json;
    },

    // traverse through json tree
    // and replace "UL > LI > P > CHILD1, CHILD2" with "UL > LI > CHILD1, CHILD2"
    // and replace paragraphs with headers if paragraphs are disabled
    // and trim spaces on oneLiner content
    removeParagraphsFromListItems(json) {
      const goDeeper = (node, depth = 0) => {
        if (depth > 1000) {
          return;
        }

        const childCount = node.content?.length || 0;
        for (let i = childCount - 1; i >= 0; i -= 1) {
          const child = node.content[i];

          // trim oneLiner content
          if (node.type === 'oneLiner') {
            const first = child?.content?.[0];

            if (first) {
              first.text = first.text.replace(/^\s+/, '');
            }

            const last = child?.content?.[child.content.length - 1];

            if (last) {
              last.text = last.text.replace(/\s+$/, '');
            }

            child.content = (child?.content || []).filter(({ text }) => Boolean(text));
          }

          // replace top level paragraphs with headings if paragraphs are disabled
          if (node.type === 'doc' && child.type === 'paragraph' && this.disabledNodes.paragraph) {
            node.content.splice(i, 1, { ...child, type: 'heading', attrs: { level: 3 } });
          }

          if (node.type === 'listItem' && child.type === 'paragraph') {
            if (child.content?.length > 0) {
              // replace paragraph with its content
              node.content.splice(i, 1, ...child.content);
            } else {
              // remove empty paragraph
              node.content.splice(i);
            }
          } else {
            goDeeper(child, depth + 1);
          }
        }
      };

      goDeeper(json);
      return json;
    },

    getCleanJsonAndHtml() {
      const json = this.removeParagraphsFromListItems(
        this.removeHighlightsAndLinkAttributesFromJson(this.tiptap.getJSON()),
      );
      const html = generateHTML(json, this.tiptap.extensionManager.baseExtensions);
      return { json, html };
    },

    getBlocksForRescore({ type, content }) {
      if (type !== 'doc') {
        return [];
      }

      const nodeTypeToBlockType = {
        heading: 'header',
        bulletList: 'list',
      };

      return content.map((node) => ({
        id: uuidv4(),
        type: nodeTypeToBlockType[node.type] || 'paragraph',
        lockType: null,
        formatBlacklist: [],
        html: generateHTML({ type: 'doc', content: [node] }, this.filteredExtensions),
      }));
    },

    onCopy(event) {
      this.eventPassThroughHandler('copyContent', event);
    },

    onPaste(event) {
      this.eventPassThroughHandler('pasteContent', event);
    },
  },
};
</script>

<style scoped lang="less">
.plus-sign() {
  content: "\2795";
  color: transparent;
  font-size: 16px;
  vertical-align: middle;
  text-align: center;
}

// locked editor has the lock icon, so move nudge icon a little bit more
.tiptap-wrap :deep(.ProseMirror) {
  &[contenteditable="false"] {
    .nudge-icon {
      right: -36px;
    }
  }
}

.tiptap-wrap {
  width: 94%;

  :deep(.ProseMirror) {
    .nudge-icon {
      width: 16px;
      height: 16px;
      position: absolute;
      right: -5%;
      margin-top: 4px;
      &:before {
        background-image: url(../../../assets/icons/Icon_Nudge_Base.svg);
        width: 16px;
        height: 16px;
        display: block;
        content: attr(data-nudge-count);
        color: @basic-red;
        font-size: 10px;
        font-weight: bold;
        text-align: center;
        line-height: 16px;
      }
      &:hover:before {
        color: @white;
        background-image: url(../../../assets/icons/Icon_Nudge_Fill.svg);
      }
      &.nudge-blue-solid {
        // changing svg color for pseudo-elements is not quite easy, so let's have two more icons
        // If we're going to implement more nudge colors, please consider this solution:
        // https://stackoverflow.com/questions/21509982/change-svg-fill-color-in-before-or-after-css
        &:before {
          color: @basic-blue;
          background-image: url(../../../assets/icons/Icon_Nudge_Base_Blue.svg);
        }
        &:hover:before {
          color: @white;
          background-image: url(../../../assets/icons/Icon_Nudge_Fill_Blue.svg);
        }
      }
    }
  }

  :deep([data-highlight-id]) {
    &.hl-grey-solid, &.hl-grey-underline {
      border-bottom: 1px dashed @grey-500;
      &:hover {
        border-bottom: 1px solid @grey-500;
      }
    }

    each(@solidBorders, .(@color, @key) {
      &.hl-@{key}-solid:hover {
        border-bottom: 1px solid @color;
      }
      &.hl-@{key}-solid {
        &.insert_before_hl {
          background-color: initial;
          &:before {
            .plus-sign();
            text-shadow: 0 0 0 @color;
          }
        }
        &.insert_after_hl {
          background-color: initial;
          &:after {
            .plus-sign();
            text-shadow: 0 0 0 @color;
          }
        }
      }
    });

    each(@solidBackgrounds, .(@color, @key) {
      &.hl-@{key}-solid {
        background-color: @color;
      }
    });

    each(@solidBorders, .(@color, @key) {
      &.hl-@{key}-underline {
        border-bottom: 2px dotted @color;
        &:hover {
          border-bottom: 1px solid @color;
        }
        &.insert_before_hl {
          border-bottom: initial;
          &:before {
            .plus-sign();
            text-shadow: 0 0 0 @color;
          }
        }
        &.insert_after_hl {
          border-bottom: initial;
          &:after {
            .plus-sign();
            text-shadow: 0 0 0 @color;
          }
        }
      }
    });
  }

  &:deep(.ProseMirror) {
    &.ProseMirror-focused {
      outline: none;
    }

    h1, h2, h3, > p, > .has-nudge > p, ul, ol {
      margin-top: 8px;
    }

    h1 {
      font-size: 24px;
      line-height: 28px;
      font-weight: 700;
    }
    h2 {
      font-size: 20px;
      line-height: 28px;
      font-weight: 700;
    }
    h3 {
      font-size: 18px;
      line-height: 28px;
      font-weight: 700;
    }
    > p, .has-nudge > p {
      font-size: 16px;
      line-height: 28px;
      font-weight: 300;
    }
    > ul, .has-nudge > ul, > ol, .has-nudge > ol {
      padding: 0;
      margin-left: 16px;
    }
    > ul li, .has-nudge > ul li, > ol li, .has-nudge > ol li {
      font-size: 16px;
      line-height: 28px;
      padding-top: 0;
      padding-right: 0;
      padding-bottom: 0;
      p {
        line-height: 28px;
      }
      ul, ol {
        padding-left: 24px;
        margin-top: 0;
      }
    }
    a {
      white-space: pre-wrap;
      color: @link-default;
      text-decoration: underline;

      &:hover {
        color: @link-hover;
      }

      &:visited {
        color: @link-visited;

        &:hover {
          color: @link-hover;
        }
      }
    }

    b, strong {
      font-weight: bold;
    }

    // empty node placeholder
    .is-empty::before {
      content: attr(data-placeholder);
      float: left;
      opacity: 0.40;
      pointer-events: none;
      height: 0;
    }
    // override for list item
    ul li > p.is-empty::before {
      content: 'Add bulleted item';
    }
    ol li > p.is-empty::before {
      content: 'Add numbered item';
    }

    // show only first placeholder
    > *:not(:first-child) {
      .is-empty::before {
        content: '';
      }
      &.is-empty::before {
        content: '';
      }
    }
  }

  &.read-only {
    user-select: text;
  }
}
</style>
